/* Time is always Khao Lak time (Asia/Bangkok, GMT+7 — no DST, so the offset
   never moves).
   A guest still on New York time who opens the schedule and sees "Thursday"
   because their phone says so has been actively misled, so nothing here ever
   uses the device timezone — every format goes through Asia/Bangkok. */

export const TZ = 'Asia/Bangkok';

/* The wedding is in the future, which makes "next event" and "happening now"
   impossible to see while building. `?at=2026-11-06T10:30` pins the clock for a
   session so those states can be looked at; without it this is just Date.now(). */
const OVERRIDE_KEY = 'mv.clock';

function readOverride(): number | null {
  if (typeof window === 'undefined') return null;
  const fromUrl = new URLSearchParams(window.location.search).get('at');
  if (fromUrl) {
    const ms = Date.parse(fromUrl.includes('T') && !/[Z+]/.test(fromUrl) ? `${fromUrl}:00+07:00` : fromUrl);
    if (!Number.isNaN(ms)) {
      sessionStorage.setItem(OVERRIDE_KEY, String(ms));
      return ms;
    }
  }
  const stored = sessionStorage.getItem(OVERRIDE_KEY);
  return stored ? Number(stored) : null;
}

let override = readOverride();
let overrideSetAt = Date.now();

export function now(): number {
  if (override === null) return Date.now();
  // Let a pinned clock run forwards, so countdowns still tick.
  return override + (Date.now() - overrideSetAt);
}

export function isClockPinned(): boolean {
  return override !== null;
}

export function clearPinnedClock(): void {
  sessionStorage.removeItem(OVERRIDE_KEY);
  override = null;
  overrideSetAt = Date.now();
}

const timeFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false });
const dayFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'short' });
const dayLongFmt = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long' });
const keyFmt = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });

export const fmtTime = (iso: string | number) => timeFmt.format(new Date(iso));
export const fmtDay = (iso: string | number) => dayFmt.format(new Date(iso));
export const fmtDayLong = (iso: string | number) => dayLongFmt.format(new Date(iso));
export const dayKey = (iso: string | number) => keyFmt.format(new Date(iso));

/**
 * Which day something falls on in Khao Lak, as a whole number.
 *
 * Built from `dayKey`, so it asks the same question the rest of this file does
 * — what is the date *there* — rather than doing arithmetic on a timestamp and
 * hoping the offset lines up.
 */
const DAY_MS = 86_400_000;

const dayNumber = (ms: number) => Math.round(Date.parse(`${dayKey(ms)}T00:00:00Z`) / 86400000);

/**
 * "in 2 hours" · "in 25 minutes" · "starting now" · null once it has started.
 *
 * **Past a day, this counts calendar days in Khao Lak — sleeps, not elapsed
 * time.** It used to round elapsed hours, `Math.round(hours / 24)`, and that
 * was wrong in a way that showed: the countdown on the same screen floors a
 * duration to midnight on the 5th, this rounded a duration to a 17:30 dinner,
 * and between about 03:00 and 09:00 Khao Lak the two differed **by two whole
 * days** — "54 days" above "Next · in 56 days". Rounding also lets the label
 * overstate the wait, saying "in 56 days" for something 55 days and twelve
 * hours away, which is not what anybody means by it.
 *
 * A calendar count fixes both. It cannot overstate, it does not flip between
 * two answers as the morning wears on, and it is what "in 55 days" means when
 * somebody says it out loud: fifty-five sleeps, whatever hour it happens to be.
 * The countdown keeps its own precise duration, and the gap between them is
 * now always the remainder the countdown is already showing in its own hours
 * column.
 */
export function untilLabel(startIso: string, at = now()): string | null {
  const start = Date.parse(startIso);
  const diff = start - at;
  if (diff <= 0) return null;
  const mins = Math.round(diff / 60000);
  if (mins <= 1) return 'starting now';
  if (mins < 60) return `in ${mins} minutes`;
  const hours = Math.round(mins / 60);
  // **Branched on the real distance, not on the rounded hours.** Rounding first
  // sent anything from 23h30m upwards into the day branch, where an event later
  // the same evening came out as "in 1 day" — and needed a `max(1, …)` clamp to
  // stop it being "in 0 days". Asking the actual duration keeps those the
  // hours they are ("in 24 hours"), and makes the clamp genuinely unreachable:
  // a full day always crosses a midnight, so past this line the two dates
  // differ by at least one.
  if (diff < DAY_MS) return `in ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  const days = dayNumber(start) - dayNumber(at);
  return `in ${days} ${days === 1 ? 'day' : 'days'}`;
}

export function durationLabel(startIso: string, endIso?: string): string | null {
  if (!endIso) return null;
  const mins = Math.round((Date.parse(endIso) - Date.parse(startIso)) / 60000);
  if (mins < 60) return `${mins} minutes`;
  const hours = mins / 60;
  const rounded = Math.round(hours * 2) / 2;
  return `${rounded} ${rounded === 1 ? 'hour' : 'hours'}`;
}

/** How old a cached payload is, said plainly: "from 19:12". */
export function cacheAgeLabel(fetchedAt: number): string {
  return `from ${fmtTime(fetchedAt)}`;
}

export function relativeUploadTime(ms: number, at = now()): string {
  const diff = at - ms;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (dayKey(ms) === dayKey(at)) return `today, ${fmtTime(ms)}`;
  return `${fmtDay(ms)}, ${fmtTime(ms)}`;
}
